import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import ColorSwatch from '../../components/ColorSwatch';
import HelpTip from '../../components/HelpTip';
import PermissionLink from '../../components/PermissionLink';
import { useDispatch } from 'react-redux';
import {
    createMaterialSku,
    getMaterial,
    loadMaterialOptions,
    MaterialDetail as MaterialDetailDTO,
    MaterialSku,
    PhysGroup,
    updateMaterial,
    publishMaterial,
    UpdateMaterialPayload,
    updateMaterialPhys,
    updateMaterialSku,
    unbindMaterialSku,
} from '../../api/material';
import { CapType } from '../../api/captype';
import { alertApprovalIfPending, ApiError, imgSrc } from '../../api/request';
import ImageLightbox from '../../components/ImageLightbox';
import ImageUploader from '../../components/ImageUploader';
import {
    CategoryCascade,
    FLEXIBILITY_OPTIONS,
    RollRatioInput,
    TraitMultiSelect,
    useCategoryTree,
    useCategoryZh,
    useMaterialTraits,
} from '../../components/MaterialFieldInputs';
import { CapDevicesModal, useCapTypes } from '../../components/CapTypeBadges';
import {
    PublishBadge,
    PublishToggle,
    isPublishedValue,
} from '../../components/PublishBadge';
import Modal from '../../components/Modal';
import DictSelect, { useDictText } from '../../components/DictSelect';
import LinkSkuModal from './LinkSkuModal';
import MaterialCapTypesCard from './MaterialCapTypesCard';
import MaterialTipsCard from './MaterialTipsCard';
import SkuQrModal, { SkuQrTarget } from './SkuQrModal';
import { actions, useCan } from '../../store';
import { listGallery } from '../../api/gallery';
import I18nEditor from '../../components/I18nEditor';
import SearchSelect from '../../components/SearchSelect';
import { I18n, i18nText, sortLangKeys } from '../../utils/i18n';
import MatrixGroupEditor, {
    loadMatrixRows,
    MatrixParams,
    MatrixRow,
} from '../../components/MatrixGroupEditor';
import { confirmDialog, alertDialog } from '../../utils/dialog';
import {
    coerceSchemaFieldValue,
    ExtFieldValue,
    extValueToInput,
    SchemaFieldInput,
    schemaFieldText,
    useDictOptions,
} from '../device/deviceSchemaForm';
import { SkuPropSelect, SkuPropTag } from '../../components/SkuProp';
import { SkuSpecInput, SkuSpecText } from '../../components/SkuSpec';
import {
    EMPTY_SKU_SPEC,
    specDraftOf,
    SkuSpecDraft,
    useSkuSchemas,
    validateSkuSpec,
} from '../../utils/skuSpec';
import { FLEXIBILITY_EMPTY_LABEL, FLEXIBILITY_SELECT_OPTS, SKU_STATUS_OPTS } from './selectOptions';

function InfoRow({ k, v }: { k: string; v: React.ReactNode }) {
    return (
        <div className="flex justify-between text-xs border-b border-slate-50 py-1">
            <span className="text-slate-400">{k}</span>
            <span className="text-slate-700">{v ?? '—'}</span>
        </div>
    );
}

// 基础信息编辑区// 基础信息编辑区: 一组 grid 表单, 复用与 MaterialCreateModal 相同的字段集.
// draft 通过 onChange 受控更新, 保存/取消由父组件回调.
function BasicEditor({
    draft,
    onChange,
    busy,
    onSave,
    onCancel,
}: {
    draft: UpdateMaterialPayload;
    onChange: (next: UpdateMaterialPayload) => void;
    busy: boolean;
    onSave: () => void | Promise<void>;
    onCancel: () => void;
}) {
    const set = <K extends keyof UpdateMaterialPayload>(
        k: K,
        v: UpdateMaterialPayload[K],
    ) => onChange({ ...draft, [k]: v });
    const categoryTree = useCategoryTree();
    const materialTraits = useMaterialTraits();
    const inputCls = 'inp w-full text-xs py-1';
    const selectCls = 'sel w-full text-xs py-1';
    const labelCls = 'text-[11px] text-slate-400';
    return (
        <div className="space-y-2 pt-1">
            <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                    <div className={labelCls}>名称 (多语言)</div>
                    <I18nEditor
                        value={draft.name || { zh: '', en: '' }}
                        onChange={(next) => set('name', next)}
                        inputCls={inputCls}
                    />
                </div>
                <div className="col-span-2">
                    <div className={labelCls}>主图</div>
                    <ImageUploader
                        value={draft.image}
                        onChange={(url) => set('image', url)}
                    />
                </div>
                <div className="col-span-2">
                    <div className={labelCls}>
                        贴图文件 (jpg / png / svg / webp, 不超过 50MB)
                    </div>
                    <ImageUploader
                        value={draft.textureUrl}
                        onChange={(url) => set('textureUrl', url)}
                        placeholder="上传贴图"
                        purpose="texture"
                    />
                </div>
                <div className="col-span-2">
                    <div className={labelCls}>
                        类别 (一级 / 二级, 来源于材料类别管理)
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <CategoryCascade
                            tree={categoryTree}
                            type1={draft.type1 || ''}
                            type2={draft.type2 || ''}
                            onChange={(t1, t2) =>
                                onChange({ ...draft, type1: t1, type2: t2 })
                            }
                            selectCls={selectCls}
                        />
                    </div>
                </div>
                <div className="col-span-2">
                    <div className={labelCls}>
                        材料ID (新建时平台自动发号, 自 10000 起递增; 小于 10000
                        为旧库 xcs 迁移号, 可人工订正)
                    </div>
                    <input
                        type="number"
                        min={0}
                        step={1}
                        className={inputCls + ' font-mono'}
                        value={draft.legacyMid ? draft.legacyMid : ''}
                        onChange={(e) => {
                            // 后端是 int64: 非法/小数/负数一律归 0 (= 清除映射)
                            const n = Math.trunc(Number(e.target.value));
                            set(
                                'legacyMid',
                                Number.isFinite(n) && n > 0 ? n : 0,
                            );
                        }}
                        placeholder="未映射"
                    />
                </div>
                <div>
                    <div className={labelCls}>工艺 1</div>
                    <DictSelect
                        category="craft"
                        className={selectCls}
                        value={draft.craft1 || ''}
                        onChange={(v) => set('craft1', v)}
                    />
                </div>
                <div>
                    <div className={labelCls}>工艺 2</div>
                    <DictSelect
                        category="craft"
                        className={selectCls}
                        value={draft.craft2 || ''}
                        onChange={(v) => set('craft2', v)}
                    />
                </div>
                <div>
                    <div className={labelCls}>表面 1</div>
                    <DictSelect
                        category="surface"
                        className={selectCls}
                        value={draft.surface1 || ''}
                        onChange={(v) => set('surface1', v)}
                    />
                </div>
                <div>
                    <div className={labelCls}>表面 2</div>
                    <DictSelect
                        category="surface"
                        className={selectCls}
                        value={draft.surface2 || ''}
                        onChange={(v) => set('surface2', v)}
                    />
                </div>
                <div>
                    <div className={labelCls}>颜色</div>
                    <DictSelect
                        category="color"
                        className={selectCls}
                        value={draft.color || ''}
                        onChange={(v) => set('color', v)}
                    />
                </div>
                <div>
                    <div className={labelCls}>颜色 HEX</div>
                    <input
                        className={inputCls + ' font-mono'}
                        value={draft.colorHex || ''}
                        onChange={(e) => set('colorHex', e.target.value)}
                        placeholder="#0A0A0A"
                    />
                </div>
                <div>
                    <div className={labelCls}>加工目标</div>
                    <input
                        className={inputCls}
                        value={draft.target || ''}
                        onChange={(e) => set('target', e.target.value)}
                    />
                </div>
                <div>
                    <div className={labelCls}>厚度 (mm)</div>
                    <input
                        type="number"
                        step="0.1"
                        className={inputCls}
                        value={draft.thickness ?? ''}
                        onChange={(e) =>
                            set(
                                'thickness',
                                e.target.value === ''
                                    ? undefined
                                    : Number(e.target.value),
                            )
                        }
                    />
                </div>
                <div>
                    <div className={labelCls}>涂层厚度 (mm)</div>
                    <input
                        type="number"
                        step="0.001"
                        className={inputCls}
                        value={draft.coatingMm ?? ''}
                        onChange={(e) =>
                            set(
                                'coatingMm',
                                e.target.value === ''
                                    ? undefined
                                    : Number(e.target.value),
                            )
                        }
                    />
                </div>
                <div>
                    <div className={labelCls}>光泽度</div>
                    <input
                        className={inputCls}
                        value={draft.gloss || ''}
                        onChange={(e) => set('gloss', e.target.value)}
                    />
                </div>
                <div>
                    <div className={labelCls}>状态</div>
                    <SearchSelect
                        className="w-full"
                        value={draft.status || 'on_shelf'}
                        onChange={(v) => set('status', v)}
                        options={SKU_STATUS_OPTS}
                    />
                </div>
                <div className="col-span-2">
                    <div className={labelCls}>
                        特性备注 (自由文本, 逗号分隔)
                    </div>
                    <input
                        className={inputCls}
                        value={draft.traits || ''}
                        onChange={(e) => set('traits', e.target.value)}
                        placeholder="受控标签请用'材料特性'多选; 此处仅补充备注"
                    />
                </div>
                <div className="col-span-2">
                    <div className={labelCls}>
                        材料使用说明 (标题 + 链接, 留空则用一级分类的)
                    </div>
                    <input
                        className={`${inputCls} mb-2`}
                        value={draft.usageDocTitle || ''}
                        onChange={(e) => set('usageDocTitle', e.target.value)}
                        placeholder="展示标题, 例: 亚克力切割教程"
                    />
                    <input
                        className={inputCls}
                        value={draft.usageDocUrl || ''}
                        onChange={(e) => set('usageDocUrl', e.target.value)}
                        placeholder="https://… 留空 = 跳一级分类配的教程 (标题一并回退)"
                    />
                </div>
                <div>
                    <div className={labelCls}>卷料传送比</div>
                    <RollRatioInput
                        className={inputCls}
                        value={draft.axisYTransmissionRatioRollMaterial}
                        onChange={(v) =>
                            set('axisYTransmissionRatioRollMaterial', v)
                        }
                    />
                </div>
                <div>
                    <div className={labelCls}>材料柔韧性</div>
                    <SearchSelect
                        className="w-full"
                        value={draft.flexibility || ''}
                        onChange={(v) => set('flexibility', v)}
                        placeholder={FLEXIBILITY_EMPTY_LABEL}
                        options={FLEXIBILITY_SELECT_OPTS}
                        clearable
                    />
                </div>
                <div className="col-span-2">
                    <div className={labelCls}>材料特性 (多选)</div>
                    <TraitMultiSelect
                        traits={materialTraits}
                        value={draft.characteristics || []}
                        onChange={(codes) => set('characteristics', codes)}
                    />
                </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
                <button
                    type="button"
                    disabled={busy}
                    className="text-xs px-3 py-1 rounded border border-slate-200 hover:bg-slate-50"
                    onClick={onCancel}
                >
                    取消
                </button>
                <button
                    type="button"
                    disabled={busy}
                    className="text-xs px-3 py-1 rounded bg-brand-600 text-white hover:bg-brand-700"
                    onClick={onSave}
                >
                    {busy ? '保存中…' : '保存'}
                </button>
            </div>
        </div>
    );
}

// SKU 新建/编辑表单 (弹窗). 编辑时 skuCode 只读; 属性/CMS耗材ID/中英文名可改.
interface SkuFormState {
    mode: 'create' | 'edit';
    /** 编辑时的原 SKU (保留 productName 其余语种等) */
    editing?: MaterialSku;
    skuCode: string;
    zh: string;
    en: string;
    prop: string;
    cmsId: string;
    status: string;
    /** 规格 (类型 + 字段原文), 原材料必填 */
    spec: SkuSpecDraft;
}

// 理化参数组编辑草稿: 组 + 各字段的输入字符串 (空串 = 不设置)
interface PhysEditState {
    group: PhysGroup;
    values: Record<string, string>;
}

/**
 * CompletenessBar 材料完成度: 理化 / SKU / 参数 / 贴士 四项缺口一眼可见, 每项带补录入口.
 * 创建向导允许整步跳过, 没有这条提示, 跳过的数据就只能靠人记得回来补.
 * 全齐时不显示 —— 不给已经做完的材料留一条恒久的提示条.
 */
function CompletenessBar({ detail }: { detail: MaterialDetailDTO }) {
    const physTotal = (detail.physGroups || []).length;
    const physFilled = (detail.physGroups || []).filter(
        (g) => g.params && Object.keys(g.params).length > 0,
    ).length;
    const caps = (detail.capability || []).filter((c) => c.isEnabled).length;
    const gaps: { label: string; hint: string }[] = [];
    if (physTotal > 0 && physFilled < physTotal) {
        gaps.push({
            label: `理化 ${physFilled}/${physTotal} 组`,
            hint: '在下方「理化参数」卡片逐组补录',
        });
    }
    if (!detail.skuCount)
        gaps.push({ label: 'SKU 0', hint: '在下方「关联 SKU」添加' });
    if (!caps)
        gaps.push({ label: '推荐工艺类型 0', hint: '在下方「适配工艺类型」展开设置推荐' });
    if (!detail.relatedParamCount) {
        gaps.push({ label: '加工参数 0 行', hint: '去参数管理页录入' });
    }
    if (gaps.length === 0) return null;
    return (
        <div className="warnbox text-xs flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-medium">待补完:</span>
            {gaps.map((g) => (
                <span key={g.label} title={g.hint}>
                    {g.label}
                </span>
            ))}
            <span className="opacity-70">(悬停看补录位置)</span>
        </div>
    );
}

function MaterialDetail() {
    const canUpdate = useCan('material', 'update');
    const canCreateBundle = useCan('material_bundle', 'create');
    // 跨域板块按各自资源的查看权决定是否渲染 (2026-09-12): 只有材料只读没有效果图/参数权的人看不到这些板块 (挂载的小贴士随材料数据显示)
    const canSeeGallery = useCan('gallery', 'get');
    const canSeeParam = useCan('param_row', 'get');
    const { xcode } = useParams<{ xcode: string }>();
    const history = useHistory();
    const dispatch = useDispatch();
    // 材料特性 code → 中文 label (展示用)
    const materialTraits = useMaterialTraits();
    // 字典受控字段只读展示中文 label, 不露 code (颜色存 BLACK/GRAY 等枚举; 工艺/表面同为字典类目)
    const colorText = useDictText('color');
    const craftText = useDictText('craft');
    const surfaceText = useDictText('surface');
    // 分类编码 → 中文 (展示用)
    const { cat1Zh, cat2Zh } = useCategoryZh();
    // 工艺类型定义 (Schema 化, 替代旧硬编码 CAP_DEFS)
    const { capTypes } = useCapTypes();
    const [detail, setDetail] = useState<MaterialDetailDTO | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // 各子区域的本地编辑状态
    const [skuForm, setSkuForm] = useState<SkuFormState | null>(null);
    const { byKey: specSchemas } = useSkuSchemas();
    // 二维码贴纸弹窗目标 (2026-09-07): 详情页入口固定按当前材料出码 (材质编码 = 本页 xcode, 不看主绑定)
    const [qrTarget, setQrTarget] = useState<SkuQrTarget | null>(null);
    // 关联已有 SKU 弹窗 / SKU 删除确认弹窗 (2026-08-21)
    const [showLinkSku, setShowLinkSku] = useState(false);
    const [unbindSku, setUnbindSku] = useState<MaterialSku | null>(null);
    // 矩阵布局字段组的行 (按 rowSource 枚举拉取): {rowSource: [{code,label}]}
    const [matrixRows, setMatrixRows] = useState<Record<string, MatrixRow[]>>(
        {},
    );
    const [physEdit, setPhysEdit] = useState<PhysEditState | null>(null);
    const physDictOpts = useDictOptions(
        (detail?.physGroups || []).flatMap((g) => g.fields.map((f) => f.optionsSource)),
    );
    // 基础信息编辑态; null = 显示模式, 对象 = 编辑模式 (持有可编辑字段的草稿)
    const [basicEdit, setBasicEdit] = useState<UpdateMaterialPayload | null>(
        null,
    );
    const [busy, setBusy] = useState(false);
    // 主图点击放大
    const [zoomSrc, setZoomSrc] = useState<string | null>(null);
    // 点击工艺类型徽标 → 具备该能力的设备弹窗
    const [openCap, setOpenCap] = useState<CapType | null>(null);

    const refresh = async () => {
        if (!xcode) return;
        const d = await getMaterial(xcode);
        setDetail(d);
    };

    // 矩阵组的行来源字典按需加载 (每类目一次)
    useEffect(() => {
        const srcs = Array.from(
            new Set(
                (detail?.physGroups || [])
                    .filter((g) => g.layout === 'matrix' && g.rowSource)
                    .map((g) => g.rowSource as string),
            ),
        );
        srcs.forEach((src) => {
            if (matrixRows[src]) return;
            loadMatrixRows(src)
                .then((rows) =>
                    setMatrixRows((prev) => ({ ...prev, [src]: rows })),
                )
                .catch(() => {});
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detail]);

    // 当前路由参数同步到 store, 让"材料详情"子 tab 在 list 页保留上次进入的材料 id
    useEffect(() => {
        if (xcode) dispatch(actions.material.setCur(xcode));
    }, [xcode, dispatch]);

    // 关联资产计数: 效果图 / 矩阵图 各一发 pageSize=1 只取 totalCount
    const [galleryCounts, setGalleryCounts] = useState<{
        effect: number;
        matrix: number;
    } | null>(null);
    useEffect(() => {
        if (!xcode) return;
        let alive = true;
        Promise.all([
            listGallery({ xcode, hasMatrix: 'no', page: 1, pageSize: 1 })
                .then((r) => r.totalCount)
                .catch(() => 0),
            listGallery({ xcode, hasMatrix: 'yes', page: 1, pageSize: 1 })
                .then((r) => r.totalCount)
                .catch(() => 0),
        ]).then(([effect, matrix]) => {
            if (alive) setGalleryCounts({ effect, matrix });
        });
        return () => {
            alive = false;
        };
    }, [xcode]);

    useEffect(() => {
        if (!xcode) return;
        setLoading(true);
        getMaterial(xcode)
            .then((d) => {
                setDetail(d);
                setError('');
            })
            .catch((e) => {
                const msg =
                    e instanceof ApiError
                        ? `${e.code}: ${e.message}`
                        : String(e);
                setError(msg);
                setDetail(null);
            })
            .finally(() => setLoading(false));
    }, [xcode]);

    const onSwitchMaterial = (id: string) => {
        if (id && id !== xcode) {
            history.push(`/materials/${encodeURIComponent(id)}`);
        }
    };

    if (loading) {
        return <div className="text-sm text-slate-500">加载中…</div>;
    }
    if (error) {
        return (
            <div className="card p-4 text-sm text-rose-600 border-rose-200 bg-rose-50">
                {error}
            </div>
        );
    }
    if (!detail) {
        return <div className="text-sm text-slate-500">未找到材料</div>;
    }

    const onSavePhys = async () => {
        if (!xcode || !physEdit) return;
        const params: Record<string, ExtFieldValue> = {};
        for (const f of physEdit.group.fields) {
            const raw = (physEdit.values[f.key] ?? '').trim();
            if (raw === '') continue;
            // 按字段类型转值 (枚举/str 存字符串, bool 存 true/false, 其余数值), 与材料向导同口径
            const v = coerceSchemaFieldValue(f, raw);
            if (typeof v === 'string' && !(f.options?.length || f.optionsSource || f.type === 'str')) {
                alertDialog(`「${f.label}」不是有效${f.type === 'bool' ? '是/否值' : '数字'}: ${raw}`);
                return;
            }
            params[f.key] = v;
        }
        setBusy(true);
        try {
            const res = await updateMaterialPhys(
                xcode,
                physEdit.group.groupKey,
                params,
            );
            // 走审批流时数据未变, refresh 只是回读原值
            await refresh();
            setPhysEdit(null);
            alertApprovalIfPending(res.message);
        } catch (e) {
            const m =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            alertDialog('保存失败: ' + m);
        } finally {
            setBusy(false);
        }
    };

    const onSaveSku = async () => {
        if (!xcode || !skuForm) return;
        if (skuForm.mode === 'create' && !skuForm.skuCode.trim()) {
            alertDialog('SKU 编码必填');
            return;
        }
        // 组装多语言产品名: 编辑时保留原有其余语种, zh/en 以表单为准 (清空即删除)
        const productName: I18n = { ...(skuForm.editing?.productName || {}) };
        if (skuForm.zh.trim()) productName.zh = skuForm.zh.trim();
        else delete productName.zh;
        if (skuForm.en.trim()) productName.en = skuForm.en.trim();
        else delete productName.en;
        const cmsId = skuForm.cmsId.trim() === '' ? 0 : Number(skuForm.cmsId);
        if (!Number.isFinite(cmsId) || cmsId < 0) {
            alertDialog('CMS 耗材ID 必须是非负整数');
            return;
        }
        const [specPayload, specErr] = validateSkuSpec(
            skuForm.prop,
            skuForm.spec,
            specSchemas,
        );
        if (specErr) {
            alertDialog(specErr);
            return;
        }
        setBusy(true);
        try {
            let message: string;
            if (skuForm.mode === 'create') {
                const res = await createMaterialSku(xcode, {
                    skuCode: skuForm.skuCode.trim(),
                    productName: Object.keys(productName).length
                        ? productName
                        : undefined,
                    prop: skuForm.prop || undefined,
                    cmsId: cmsId || undefined,
                    ...(specPayload.specGroup ? specPayload : {}),
                });
                message = res.message;
            } else {
                const res = await updateMaterialSku(xcode, skuForm.skuCode, {
                    productName,
                    prop: skuForm.prop,
                    cmsId,
                    status: skuForm.status,
                    ...specPayload,
                });
                message = res.message;
            }
            // 走审批流时数据未变, refresh 只是回读原值
            await refresh();
            setSkuForm(null);
            alertApprovalIfPending(message);
        } catch (e) {
            const m =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            alertDialog('保存失败: ' + m);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-5">
            <div className="flex items-center gap-3 text-sm flex-wrap">
                <a
                    className="text-brand-600 hover:underline cursor-pointer"
                    onClick={() => {
                        // 从列表进来时 goBack 还原列表的页码/筛选; 直链打开详情时兜底回列表首页
                        if (window.history.length > 1) history.goBack();
                        else history.push('/materials');
                    }}
                >
                    ← 材料库
                </a>
                <span className="text-slate-300">·</span>
                <span className="font-mono text-xs text-slate-500">
                    {detail.xcode}
                </span>
                <span className="text-slate-300">·</span>
                <SearchSelect
                    className="w-72"
                    value={detail.xcode}
                    valueLabel={`${detail.xcode} · ${i18nText(detail.name)}`}
                    onChange={onSwitchMaterial}
                    placeholder="搜 材质编码 / 名称"
                    loadOptions={loadMaterialOptions}
                />
                {/* 以此为模板新建 (2026-09-16): 同材质换颜色/换厚度是最高频的建材场景 ——
                    带 ?from= 进向导, 预填类别/属性/理化/能力位, 编码与名称留空重填. */}
                <button
                    type="button"
                    className="ml-auto btn-ghost"
                    title="用本材料的类别/属性/理化/能力位预填一个新材料 (编码与名称重新填)"
                    onClick={() =>
                        history.push(
                            `/materials/new?from=${encodeURIComponent(detail.xcode)}`,
                        )
                    }
                    hidden={!canCreateBundle}
                >
                    ⧉ 以此为模板新建
                </button>
            </div>

            {/* 完成度引导: 向导允许跳过理化/SKU/参数, 这里把缺口摆出来并给补录入口,
                否则跳过的那些数据永远没人回来补 (设计 07 S2). */}
            <CompletenessBar detail={detail} />

            <div className="grid grid-cols-3 gap-4">
                <div className="card p-4 col-span-1 space-y-1.5">
                    <div className="text-sm font-semibold flex items-center gap-2">
                        基础信息
                        <span className="tag bg-brand-50 text-brand-700">
                            {detail.xcode}
                        </span>
                        {basicEdit === null && (
                            <button
                                type="button"
                                className="ml-auto text-xs text-brand-600 hover:underline"
                                onClick={() =>
                                    setBasicEdit({
                                        name: { ...detail.name },
                                        legacyMid: detail.legacyMid || 0,
                                        image: detail.image || '',
                                        type1: detail.type1 || '',
                                        type2: detail.type2 || '',
                                        craft1: detail.craft1 || '',
                                        craft2: detail.craft2 || '',
                                        surface1: detail.surface1 || '',
                                        surface2: detail.surface2 || '',
                                        color: detail.color || '',
                                        colorHex: detail.colorHex || '',
                                        target: detail.target || '',
                                        coatingMm: detail.coatingMm,
                                        gloss: detail.gloss || '',
                                        traits: detail.traits || '',
                                        usageDocUrl: detail.usageDocUrl || '',
                                        usageDocTitle:
                                            detail.usageDocTitle || '',
                                        textureUrl: detail.textureUrl || '',
                                        axisYTransmissionRatioRollMaterial:
                                            detail.axisYTransmissionRatioRollMaterial ??
                                            undefined,
                                        flexibility: detail.flexibility || '',
                                        characteristics:
                                            detail.characteristics || [],
                                        status: detail.status,
                                    })
                                }
                                hidden={!canUpdate}
                            >
                                ✎ 编辑
                            </button>
                        )}
                    </div>
                    {basicEdit === null &&
                        (detail.image || detail.textureUrl) && (
                            // 主图与贴图并排, 下方标签区分: 主图 = 商品展示图, 贴图 = 渲染用表面素材
                            <div className="flex justify-center items-start gap-6 py-2">
                                {detail.image && (
                                    <figure className="flex flex-col items-center gap-1.5">
                                        <img
                                            src={imgSrc(detail.image)}
                                            alt={i18nText(detail.name)}
                                            className="h-40 w-40 rounded border border-slate-200 object-contain cursor-zoom-in hover:ring-2 hover:ring-brand-300"
                                            onClick={() =>
                                                setZoomSrc(imgSrc(detail.image))
                                            }
                                            onError={(e) => {
                                                (
                                                    e.currentTarget as HTMLImageElement
                                                ).style.display = 'none';
                                            }}
                                        />
                                        <figcaption className="tag bg-slate-100 text-slate-600">
                                            主图
                                        </figcaption>
                                    </figure>
                                )}
                                {detail.textureUrl && (
                                    <figure className="flex flex-col items-center gap-1.5">
                                        <img
                                            src={imgSrc(detail.textureUrl)}
                                            alt="贴图"
                                            className="h-40 w-40 rounded border border-slate-200 object-cover bg-slate-50 cursor-zoom-in hover:ring-2 hover:ring-brand-300"
                                            onClick={() =>
                                                setZoomSrc(
                                                    imgSrc(detail.textureUrl),
                                                )
                                            }
                                        />
                                        <figcaption
                                            className="tag bg-amber-50 text-amber-700"
                                            title="贴图文件: 材料表面渲染素材 (jpg / png / svg / webp, 不超过 50MB), 不是商品展示图"
                                        >
                                            贴图
                                        </figcaption>
                                    </figure>
                                )}
                            </div>
                        )}
                    {basicEdit !== null ? (
                        <BasicEditor
                            draft={basicEdit}
                            onChange={setBasicEdit}
                            busy={busy}
                            onCancel={() => setBasicEdit(null)}
                            onSave={async () => {
                                if (!xcode) return;
                                // 改一级类别 = 改绑理化模板: 后端会清空该材料原模板下已填的理化参数值
                                if (
                                    basicEdit.type1 !== undefined &&
                                    basicEdit.type1 !== detail.type1 &&
                                    !(await confirmDialog(
                                        `确认把 ${xcode} 的一级类别从 ${detail.type1} 改为 ${basicEdit.type1}?\n` +
                                            '该材料在原类别模板下已填的理化参数值会被清空且无法恢复, 需按新模板重填.',
                                    ))
                                ) {
                                    return;
                                }
                                setBusy(true);
                                try {
                                    const res = await updateMaterial(
                                        xcode,
                                        basicEdit,
                                    );
                                    // 走审批流时数据未变, refresh 只是回读原值
                                    await refresh();
                                    setBasicEdit(null);
                                    alertApprovalIfPending(res.message);
                                } catch (e) {
                                    const msg =
                                        e instanceof ApiError
                                            ? `${e.code}: ${e.message}`
                                            : String(e);

                                    alertDialog('保存失败: ' + msg);
                                } finally {
                                    setBusy(false);
                                }
                            }}
                        />
                    ) : (
                        <>
                            <InfoRow k="名称" v={i18nText(detail.name)} />
                            <InfoRow k="英文" v={detail.name?.en} />
                            <InfoRow
                                k="语种"
                                v={`${Object.keys(detail.name || {}).length} 种 (${sortLangKeys(Object.keys(detail.name || {})).join(', ') || '—'})`}
                            />
                            <InfoRow
                                k="发布状态"
                                v={
                                    <span className="inline-flex items-center gap-2">
                                        <PublishBadge
                                            isPublished={detail.isPublished}
                                            publishedAt={detail.publishedAt}
                                            layout="inline"
                                        />
                                        <PublishToggle
                                            resource="material"
                                            published={isPublishedValue(
                                                detail.isPublished,
                                            )}
                                            label={detail.xcode}
                                            onToggle={(p) =>
                                                publishMaterial(detail.xcode, p)
                                            }
                                            onDone={refresh}
                                        />
                                    </span>
                                }
                            />
                            <InfoRow
                                k="一级分类"
                                v={cat1Zh(detail.type1) || '—'}
                            />
                            <InfoRow
                                k="二级分类"
                                v={cat2Zh(detail.type1, detail.type2) || '—'}
                            />
                            <InfoRow
                                k="材料ID"
                                v={
                                    detail.legacyMid ? (
                                        <span
                                            className="font-mono"
                                            title="材料ID (legacy_mid): 平台自动发号 (>=10000) 或旧库 xcs 迁移号 (<10000)"
                                        >
                                            {detail.legacyMid}
                                        </span>
                                    ) : (
                                        '—'
                                    )
                                }
                            />
                            <InfoRow
                                k="工艺"
                                v={`${craftText(detail.craft1)} / ${craftText(detail.craft2)}`}
                            />
                            <InfoRow
                                k="表面处理"
                                v={`${surfaceText(detail.surface1)} / ${surfaceText(detail.surface2)}`}
                            />
                            <InfoRow
                                k="颜色"
                                v={
                                    <ColorSwatch
                                        hex={detail.colorHex}
                                        label={colorText(detail.color)}
                                        showHex
                                        size={12}
                                    />
                                }
                            />
                            <InfoRow k="加工目标" v={detail.target} />
                            <InfoRow
                                k="厚度"
                                v={
                                    detail.thickness
                                        ? `${detail.thickness} mm`
                                        : '—'
                                }
                            />
                            <InfoRow
                                k="涂层厚度"
                                v={
                                    detail.coatingMm
                                        ? `${detail.coatingMm} mm`
                                        : '—'
                                }
                            />
                            <InfoRow k="光泽度" v={detail.gloss} />
                            <InfoRow
                                k="特性备注"
                                v={
                                    detail.traits ? (
                                        <span className="flex flex-wrap gap-1 justify-end">
                                            {detail.traits.split(/[,，]/).map(
                                                (t) =>
                                                    t.trim() && (
                                                        <span
                                                            key={t}
                                                            className="tag bg-amber-50 text-amber-700"
                                                        >
                                                            {t.trim()}
                                                        </span>
                                                    ),
                                            )}
                                        </span>
                                    ) : (
                                        '—'
                                    )
                                }
                            />
                            <InfoRow
                                k="使用说明"
                                v={
                                    detail.usageDocUrlEffective ? (
                                        <a
                                            href={detail.usageDocUrlEffective}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-brand-600 hover:underline"
                                            title={detail.usageDocUrlEffective}
                                        >
                                            {detail.usageDocTitleEffective ||
                                                '查看教程'}{' '}
                                            ↗
                                            {!detail.usageDocUrl && (
                                                <span
                                                    className="ml-1 text-[11px] text-slate-400"
                                                    title={`未单独配置, 取一级分类 ${detail.type1 || ''} 的默认链接`}
                                                >
                                                    (继承一级分类)
                                                </span>
                                            )}
                                        </a>
                                    ) : (
                                        '—'
                                    )
                                }
                            />
                            <InfoRow
                                k="卷料传送比"
                                v={
                                    detail.axisYTransmissionRatioRollMaterial ??
                                    '—'
                                }
                            />
                            <InfoRow
                                k="材料柔韧性"
                                v={
                                    detail.flexibility
                                        ? FLEXIBILITY_OPTIONS.find(
                                              (o) =>
                                                  o.value ===
                                                  detail.flexibility,
                                          )?.label || detail.flexibility
                                        : '—'
                                }
                            />
                            <InfoRow
                                k="材料特性"
                                v={
                                    detail.characteristics &&
                                    detail.characteristics.length > 0 ? (
                                        <span className="flex flex-wrap gap-1 justify-end">
                                            {detail.characteristics.map(
                                                (code) => (
                                                    <span
                                                        key={code}
                                                        className="tag bg-sky-50 text-sky-700"
                                                        title={code}
                                                    >
                                                        {materialTraits.find(
                                                            (t) =>
                                                                t.code === code,
                                                        )?.label || code}
                                                    </span>
                                                ),
                                            )}
                                        </span>
                                    ) : (
                                        '—'
                                    )
                                }
                            />
                            <InfoRow k="材质编码 (xcode)" v={detail.xcode} />
                        </>
                    )}
                </div>

                {/* 理化参数: 全部 Schema 驱动 (含矩阵布局组, 如光学参数) */}
                <div className="card p-4 col-span-2 space-y-4">
                    <div className="text-sm font-semibold flex items-center gap-2">
                        理化参数
                        <span className="tag bg-brand-50 text-brand-700 font-normal">
                            Schema 驱动 · 可自定义
                        </span>
                        {detail.type1 ? (
                            <PermissionLink
                                resource="phys_schema"
                                verb="update"
                                hide
                                to={`/materials/templates?focus=${encodeURIComponent(detail.type1)}`}
                                className="tag bg-slate-100 text-slate-500 font-normal hover:bg-brand-50 hover:text-brand-600"
                                title="本材料绑定的理化模板 (一个一级类别一个模板), 表单按模板渲染 · 点击查看/管理模板"
                            >
                                引用模板: {cat1Zh(detail.type1)} ↗
                            </PermissionLink>
                        ) : (
                            <span
                                className="tag bg-amber-50 text-amber-600 font-normal"
                                title="模板由一级分类绑定, 未分类 = 无模板"
                            >
                                未绑定模板
                            </span>
                        )}
                        <HelpTip text="理化参数是参数推荐/校验的物理底座。字段由绑定的类别模板 (= 材料一级分类) 定义, 值按材料录入; 未配置的组显示待录入。" />
                    </div>

                    {!detail.type1 && (
                        <div className="text-xs text-amber-600 bg-amber-50 rounded p-3">
                            该材料<b>未设置一级分类</b>, 无法绑定理化模板 —
                            请点左侧「基础信息 → 编辑」选择分类,
                            保存后此处将按该类别模板渲染字段。
                        </div>
                    )}

                    {(detail.physGroups || []).map((g) => {
                        if (g.layout === 'matrix') {
                            return (
                                <MatrixGroupEditor
                                    resource="material"
                                    key={g.groupKey}
                                    label={g.label}
                                    src={g.src}
                                    rowLabel={g.rowLabel}
                                    rowSource={g.rowSource}
                                    fields={g.fields}
                                    rows={matrixRows[g.rowSource || ''] || []}
                                    params={
                                        (g.params ||
                                            null) as MatrixParams | null
                                    }
                                    onSave={async (out) => {
                                        const res = await updateMaterialPhys(
                                            xcode || '',
                                            g.groupKey,
                                            out,
                                        );
                                        return res.message;
                                    }}
                                    onSaved={refresh}
                                />
                            );
                        }
                        const hasValue =
                            !!g.params && Object.keys(g.params).length > 0;
                        return (
                            <div key={g.groupKey}>
                                <div className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-2">
                                    {g.label}
                                    {g.src && (
                                        <HelpTip
                                            text={
                                                <>
                                                    数据来源:{' '}
                                                    <code>{g.src}</code>
                                                </>
                                            }
                                        />
                                    )}
                                    <button
                                        type="button"
                                        className="ml-auto text-xs text-brand-600 hover:underline font-normal"
                                        onClick={() => {
                                            const values: Record<
                                                string,
                                                string
                                            > = {};
                                            for (const f of g.fields) {
                                                const v = g.params?.[f.key];
                                                values[f.key] = extValueToInput(v);
                                            }
                                            setPhysEdit({ group: g, values });
                                        }}
                                        hidden={!canUpdate}
                                    >
                                        ✎ 编辑
                                    </button>
                                </div>
                                <div className="grid grid-cols-3 gap-x-4 gap-y-1">
                                    {g.fields.map((f) => {
                                        const v = g.params?.[f.key] as
                                            | number
                                            | string
                                            | undefined;
                                        return (
                                            <div
                                                key={f.key}
                                                className="flex justify-between text-xs border-b border-slate-50 py-1"
                                            >
                                                <span className="text-slate-400">
                                                    {f.label}
                                                    {f.unit && (
                                                        <span className="text-slate-300">
                                                            {' '}
                                                            ({f.unit})
                                                        </span>
                                                    )}
                                                </span>
                                                <span
                                                    className={
                                                        v == null
                                                            ? 'text-slate-300'
                                                            : 'text-slate-700 font-mono'
                                                    }
                                                >
                                                    {v == null ? '—' : schemaFieldText(f, v, physDictOpts)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                                {!hasValue && (
                                    <div className="text-[11px] text-amber-600 mt-1">
                                        该材质暂未配置本组参数（字段由 Schema
                                        定义，值待录入）。
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            <MaterialCapTypesCard
                xcode={detail.xcode}
                capability={detail.capability || []}
                capTypes={capTypes}
                canUpdate={canUpdate}
                onOpenCap={setOpenCap}
                onSaved={refresh}
            />

            <MaterialTipsCard detail={detail} />

            <div className="card overflow-x-auto">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                    <div className="text-sm font-semibold">
                        关联 SKU · 一物一码（{detail.skuCount}）
                    </div>
                    <div className="flex items-center gap-3">
                        {skuForm === null && (
                            <button
                                type="button"
                                className="text-xs text-brand-600 hover:underline"
                                onClick={() =>
                                    setSkuForm({
                                        mode: 'create',
                                        skuCode: '',
                                        zh: '',
                                        en: '',
                                        prop: '',
                                        cmsId: '',
                                        status: 'on_shelf',
                                        spec: EMPTY_SKU_SPEC,
                                    })
                                }
                                hidden={!canUpdate}
                            >
                                ＋ 添加 SKU
                            </button>
                        )}
                        <button
                            type="button"
                            className="text-xs text-brand-600 hover:underline"
                            title="从 SKU 库搜索已有 SKU, 把它的材质编码改绑到本材料"
                            onClick={() => setShowLinkSku(true)}
                            hidden={!canUpdate}
                        >
                            ⇄ 关联已有 SKU
                        </button>
                        <PermissionLink
                            resource="material"
                            hide
                            to={`/skus?xcode=${encodeURIComponent(detail.xcode)}`}
                            className="text-xs text-brand-600 hover:underline"
                        >
                            在 SKU 库中查看 →
                        </PermissionLink>
                    </div>
                </div>
                {(detail.skus || []).length === 0 ? (
                    <div className="px-4 py-6 text-center text-xs text-slate-400">
                        暂无 SKU
                    </div>
                ) : (
                    <table className="w-full">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className="th">SKU 编码</th>
                                <th className="th">产品名</th>
                                <th className="th">属性</th>
                                <th className="th">规格</th>
                                <th className="th">二维码</th>
                                <th className="th">操作</th>
                            </tr>
                        </thead>
                        <tbody>
                            {detail.skus.map((s) => {
                                return (
                                    <tr
                                        key={s.id}
                                        className="hover:bg-slate-50"
                                    >
                                        <td className="td font-mono text-xs">
                                            {s.skuCode}
                                        </td>
                                        <td className="td text-xs">
                                            <div>
                                                {i18nText(
                                                    s.productName || {},
                                                ) || '—'}
                                            </div>
                                            {s.productName?.en && (
                                                <div className="text-[11px] text-slate-400">
                                                    {s.productName.en}
                                                </div>
                                            )}
                                        </td>
                                        <td className="td">
                                            <SkuPropTag code={s.prop} />
                                        </td>
                                        <td className="td">
                                            <SkuSpecText sku={s} />
                                        </td>
                                        <td className="td text-xs whitespace-nowrap">
                                            <button
                                                type="button"
                                                className="text-brand-600 hover:underline"
                                                title="生成二维码贴纸 (预览 / 下载 PNG)"
                                                onClick={() =>
                                                    setQrTarget({
                                                        skuCode: s.skuCode,
                                                        productName:
                                                            s.productName,
                                                        materials: [
                                                            {
                                                                xcode: detail.xcode,
                                                                name: detail.name,
                                                                isPrimary: true,
                                                            },
                                                        ],
                                                    })
                                                }
                                            >
                                                ▦ 二维码
                                            </button>
                                        </td>
                                        <td className="td text-xs space-x-3 whitespace-nowrap">
                                            <button
                                                type="button"
                                                className="text-brand-600 hover:underline"
                                                onClick={() =>
                                                    setSkuForm({
                                                        mode: 'edit',
                                                        editing: s,
                                                        skuCode: s.skuCode,
                                                        zh:
                                                            s.productName?.zh ||
                                                            '',
                                                        en:
                                                            s.productName?.en ||
                                                            '',
                                                        prop: s.prop || '',
                                                        cmsId:
                                                            s.cmsId > 0
                                                                ? String(
                                                                      s.cmsId,
                                                                  )
                                                                : '',
                                                        status: s.status,
                                                        spec: specDraftOf(s),
                                                    })
                                                }
                                                hidden={!canUpdate}
                                            >
                                                编辑
                                            </button>
                                            {/* 材料详情只管关系: 与上方小贴士区的「移除」同语义 —— 解绑只断本材料的
                                                绑定, SKU 本体留在 SKU 库. 销毁 SKU 本体请去 SKU 库 (避免在材料
                                                页误点就毁掉 SKU 及其 CMS 映射). */}
                                            <button
                                                type="button"
                                                className="btn-danger-text"
                                                title="只解除与本材料的绑定, SKU 仍留在 SKU 库"
                                                onClick={() => setUnbindSku(s)}
                                                hidden={!canUpdate}
                                            >
                                                解绑
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
                <div className="text-[11px] text-slate-400 px-4 py-3">
                    「解绑」只解除本材料的关联, SKU 仍留在 SKU 库
                    (可再绑到其它材料); 要销毁 SKU 本体请到 SKU 库操作。
                    <br />
                    每个 SKU 为一物一码, 直接映射一个 CMS 耗材ID（即原“关联ID”,
                    两者合一）; 目标 SKU ↔ 耗材ID 一一对应。{' '}
                    <PermissionLink
                        resource="material"
                        hide
                        to="/materials/id-mapping"
                        className="text-brand-600 hover:underline"
                    >
                        查看 ID 映射 →
                    </PermissionLink>
                </div>
            </div>

            <div className="card p-4" hidden={!canSeeParam && !canSeeGallery}>
                <div className="text-sm font-semibold mb-2">
                    关联参数 / 效果图{' '}
                    <span
                        className="tag bg-emerald-50 text-emerald-700"
                        hidden={!canSeeParam}
                    >
                        {detail.relatedParamCount} 条参数
                    </span>
                </div>
                <div className="text-xs text-slate-500">
                    <PermissionLink
                        resource="param_row"
                        hide
                        to={`/param-sets?xcode=${encodeURIComponent(detail.xcode)}`}
                        className="text-brand-600 mr-3 hover:underline"
                    >
                        参数总览 →
                    </PermissionLink>
                    <PermissionLink
                        resource="gallery"
                        hide
                        to={`/gallery?xcode=${encodeURIComponent(detail.xcode)}`}
                        className="text-brand-600 mr-3 hover:underline"
                    >
                        效果图
                        {galleryCounts ? ` (${galleryCounts.effect})` : ''} →
                    </PermissionLink>
                    <PermissionLink
                        resource="gallery"
                        hide
                        to={`/gallery/matrix?xcode=${encodeURIComponent(detail.xcode)}`}
                        className="text-brand-600 hover:underline"
                    >
                        矩阵图
                        {galleryCounts ? ` (${galleryCounts.matrix})` : ''} →
                    </PermissionLink>
                </div>
            </div>

            <SkuQrModal
                open={!!qrTarget}
                target={qrTarget}
                onClose={() => setQrTarget(null)}
            />

            {/* 关联已有 SKU: 从 SKU 库改绑到本材料 */}
            {xcode && (
                <LinkSkuModal
                    open={showLinkSku}
                    xcode={xcode}
                    onClose={() => setShowLinkSku(false)}
                    onLinked={refresh}
                />
            )}

            {/* SKU 解绑: xcode 置空, SKU 留在 SKU 库 (与小贴士区的「移除」同语义).
                本页不提供"删除 SKU 本体"——关联区只管关系, 销毁本体在 SKU 库做. */}
            {unbindSku && (
                <Modal
                    open
                    width={460}
                    title="解绑 SKU"
                    onClose={() => setUnbindSku(null)}
                >
                    <div className="space-y-4 text-sm">
                        <div>
                            确认把{' '}
                            <span className="font-semibold">
                                {unbindSku.skuCode}
                            </span>{' '}
                            从本材料解绑?
                            <div className="text-xs text-slate-500 mt-1">
                                SKU 本体、CMS 映射与它在其他材料上的绑定都保留;
                                若这是它唯一的绑定, 解绑后变为「未绑定」留在 SKU
                                库, 之后可重新绑定到任意材料。
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                            <button
                                type="button"
                                className="btn-ghost"
                                disabled={busy}
                                onClick={() => setUnbindSku(null)}
                            >
                                取消
                            </button>
                            <button
                                type="button"
                                className="btn-primary disabled:opacity-50"
                                disabled={busy}
                                onClick={async () => {
                                    setBusy(true);
                                    try {
                                        // 多对多: 只解除与本材料的绑定, 其他材料的绑定不受影响
                                        const res = await unbindMaterialSku(
                                            xcode,
                                            unbindSku.skuCode,
                                        );
                                        setUnbindSku(null);
                                        await refresh();
                                        alertApprovalIfPending(res.message);
                                    } catch (e) {
                                        const m =
                                            e instanceof ApiError
                                                ? `${e.code}: ${e.message}`
                                                : String(e);
                                        alertDialog('解绑失败: ' + m);
                                    } finally {
                                        setBusy(false);
                                    }
                                }}
                            >
                                {busy ? '提交中…' : '确认解绑'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* 工艺类型徽标点击 → 具备该能力的设备 (推荐状态在卡片展开后统一设置) */}
            <CapDevicesModal cap={openCap} onClose={() => setOpenCap(null)} />

            {/* 理化参数分组编辑弹窗 (按字段类型录入, 空 = 不设置该字段) */}
            {physEdit && (
                <Modal
                    open
                    width={560}
                    title={`编辑 ${physEdit.group.label}`}
                    onClose={() => setPhysEdit(null)}
                >
                    <div className="space-y-4">
                        <div className="text-xs text-slate-500">
                            字段由 Schema「{physEdit.group.groupKey}」定义;
                            留空表示不设置该字段。
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            {physEdit.group.fields.map((f) => (
                                <div key={f.key}>
                                    <label className="text-xs text-slate-500 block mb-1">
                                        {f.label}
                                        {f.unit && (
                                            <span className="text-slate-300">
                                                {' '}
                                                ({f.unit})
                                            </span>
                                        )}
                                    </label>
                                    <SchemaFieldInput
                                        field={f}
                                        value={physEdit.values[f.key] ?? ''}
                                        dictOpts={physDictOpts}
                                        inputClassName="inp w-full font-mono"
                                        onChange={(v) =>
                                            setPhysEdit({
                                                ...physEdit,
                                                values: {
                                                    ...physEdit.values,
                                                    [f.key]: v,
                                                },
                                            })
                                        }
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                className="btn-ghost"
                                disabled={busy}
                                onClick={() => setPhysEdit(null)}
                            >
                                取消
                            </button>
                            <button
                                type="button"
                                className="btn-primary"
                                disabled={busy}
                                onClick={onSavePhys}
                            >
                                {busy ? '保存中…' : '保存'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* SKU 新建/编辑弹窗 */}
            {skuForm && (
                <Modal
                    open
                    width={520}
                    title={
                        skuForm.mode === 'create'
                            ? '添加 SKU'
                            : `编辑 SKU ${skuForm.skuCode}`
                    }
                    onClose={() => setSkuForm(null)}
                >
                    <div className="space-y-4">
                        {skuForm.mode === 'create' && (
                            <div>
                                <label className="text-xs text-slate-500 block mb-1">
                                    SKU 编码
                                </label>
                                <input
                                    className="inp w-full font-mono"
                                    placeholder="例: P01040000001"
                                    value={skuForm.skuCode}
                                    onChange={(e) =>
                                        setSkuForm({
                                            ...skuForm,
                                            skuCode: e.target.value,
                                        })
                                    }
                                />
                            </div>
                        )}
                        <div>
                            <label className="text-xs text-slate-500 block mb-1">
                                产品名 (中)
                            </label>
                            <input
                                className="inp w-full"
                                value={skuForm.zh}
                                onChange={(e) =>
                                    setSkuForm({
                                        ...skuForm,
                                        zh: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div>
                            <label className="text-xs text-slate-500 block mb-1">
                                产品名 (英)
                            </label>
                            <input
                                className="inp w-full"
                                placeholder="(可空)"
                                value={skuForm.en}
                                onChange={(e) =>
                                    setSkuForm({
                                        ...skuForm,
                                        en: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs text-slate-500 block mb-1">
                                    属性
                                </label>
                                <SkuPropSelect
                                    className="sel w-full"
                                    value={skuForm.prop}
                                    onChange={(v) =>
                                        setSkuForm({ ...skuForm, prop: v })
                                    }
                                />
                            </div>
                            <div>
                                <label className="text-xs text-slate-500 block mb-1">
                                    CMS 耗材ID
                                </label>
                                <input
                                    type="number"
                                    className="inp w-full font-mono"
                                    placeholder="0 = 待映射"
                                    value={skuForm.cmsId}
                                    onChange={(e) =>
                                        setSkuForm({
                                            ...skuForm,
                                            cmsId: e.target.value,
                                        })
                                    }
                                />
                            </div>
                        </div>
                        <SkuSpecInput
                            prop={skuForm.prop}
                            value={skuForm.spec}
                            onChange={(v) =>
                                setSkuForm({ ...skuForm, spec: v })
                            }
                        />
                        {skuForm.mode === 'edit' && (
                            <div>
                                <label className="text-xs text-slate-500 block mb-1">
                                    状态
                                </label>
                                <SearchSelect
                                    className="w-full"
                                    value={skuForm.status}
                                    onChange={(v) =>
                                        setSkuForm({
                                            ...skuForm,
                                            status: v,
                                        })
                                    }
                                    options={SKU_STATUS_OPTS}
                                />
                            </div>
                        )}
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                className="btn-ghost"
                                disabled={busy}
                                onClick={() => setSkuForm(null)}
                            >
                                取消
                            </button>
                            <button
                                type="button"
                                className="btn-primary"
                                disabled={busy}
                                onClick={onSaveSku}
                            >
                                {busy ? '保存中…' : '保存'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            <ImageLightbox
                src={zoomSrc}
                alt={i18nText(detail.name)}
                onClose={() => setZoomSrc(null)}
            />
        </div>
    );
}

export default MaterialDetail;
